import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import useSession from "Hooks/session.hook";
import { encode } from "Helpers/index";
import moment from "moment";
import { Button, DataTable } from "@life-line-lab/components-react";
import DataTableFilter from "Components/shared/data-table-filter";
import { Spin, Tooltip } from "antd";
import { LoadingOutlined, ReloadOutlined } from "@ant-design/icons";
import useCorporateType from "Hooks/corporate-type.hook";

interface DropOffListComponent {
  onFilter?: any;
  customerOptions: any;
  allOrderList: any;
  loadingMechanism: any;
  handleRefresh: any;
  filterState: any;
}

const CustomerDropOff: React.FC<DropOffListComponent> = ({
  customerOptions,
  allOrderList,
  onFilter,
  loadingMechanism,
  handleRefresh,
  filterState,
}) => {
  const { roleTypes } = useSession();
  const { t } = useTranslation();
  const history = useHistory();
  const { isLoading } = loadingMechanism;
  const { isCustomer } = useCorporateType();

  const handleOpenDropoffTrackingView = (dropoffID) => {
    let encodedId = encode(dropoffID);
    history.push(
      `/delivery-management/parcel-tracking/dropoff/${encodedId}/view`,
    );
  };

  const initialFilterSelection = [
    {
      key: "customer",
      name: "customersList",
      label: t("placeholder.filterCustomer"),
      placeholder: t("placeholder.filterCustomer"),
      value: filterState?.customersList,
      options: [{ label: t("option.all"), value: "" }].concat(
        customerOptions?.map((org) => {
          return {
            label: org.name || "",
            value: org._id,
          };
        }),
      ),
    },
  ];

  let columns: any = [
    {
      title: t("label.dropoffNo").toLocaleUpperCase(),
      dataIndex: "dropOffCode",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <a className="clickable-id">{record.dropOffCode || ""}</a>;
      },
      onCell: (record, rowIndex) => {
        return {
          onClick: (e: React.MouseEvent<HTMLElement>) => {
            handleOpenDropoffTrackingView(record._id);
          },
        };
      },
    },
    {
      title: t("label.dropoffDateEta").toLocaleUpperCase(),
      dataIndex: "dropOffDate",
      width: 150,
      ellipsis: true,
      render: (_: any, record: any) => {
        return record.dropOffDate ? (
          <span>
            {record.dropOffDate?.substr(0, record.dropOffDate.indexOf("T")) +
              " | " +
              moment(record.dropOffTimeFrom).format("hh:mm a")}
          </span>
        ) : (
          <span>&#11835;</span>
        );
      },
    },
    {
      title: t("label.totalParcels").toLocaleUpperCase(),
      dataIndex: "noOfParcels",
      width: 100,
      ellipsis: true,
      render: (_: any, record: any) => {
        return <span>{record?.parcelList?.length}</span>;
      },
    },
    {
      title: t("label.customer").toLocaleUpperCase(),
      dataIndex: "merchantName",
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.vehicleExpected").toLocaleUpperCase(),
      dataIndex: "noOfDrivers",
      width: 105,
      ellipsis: true,
    },
    {
      title: t("label.weight").toLocaleUpperCase(),
      dataIndex: "totalWeight",
      width: 105,
    },
  ];

  const filteredColumns = () => {
    columns.splice(3, 1);
    return columns;
  };

  const handleSearch = async (text: string) => {
    onFilter({ orFields: text });
  };

  const handleFilterSelection = (selection) => {
    onFilter({ customersList: selection.customersList });
  };

  return (
    <div className="parcel-order-list">
      <div className="dm-header">
        <div className="filter-component">
          <DataTableFilter
            searchPlaceholder={t("placeholder.dropoffSearchbar")}
            onSearch={(text: string) => {
              handleSearch(text);
            }}
            onFilter={(selection) => handleFilterSelection(selection)}
            renderFilterSelection={!isCustomer ? initialFilterSelection : []}
          />
          <div className="dm-buttons-container">
            <div className="dm-action-btn-container alignment">
              <Tooltip title="Refresh" placement="left">
                <Button
                  icon={isLoading ? <LoadingOutlined /> : <ReloadOutlined />}
                  isDisabled={isLoading}
                  onClick={() => handleRefresh()}
                  shape="circle"
                />
              </Tooltip>
            </div>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="loading-container">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
      ) : (
        <>
          <DataTable
            rowKey={(record: any) => {
              return record._id;
            }}
            columns={
              roleTypes == "CUSTOMER-SUPER_ADMIN" ? filteredColumns() : columns
            }
            dataSource={allOrderList || []}
            scroll={{ x: true }}
          />
        </>
      )}
    </div>
  );
};

export default CustomerDropOff;
